import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Globe, Smartphone, Eye, EyeOff, Users } from 'lucide-react';
import { get12MonthProductId } from '../../config/homeProductsConfig';

export default function ProductComparisonTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 产品对比数据
  const products = [
    {
      id: 240,
      nameKey: 'homepage.comparisonTable.products.gptOfficial.name',
      loginMethodKey: 'homepage.comparisonTable.products.gptOfficial.loginMethod',
      privacyKey: 'homepage.comparisonTable.products.gptOfficial.privacy',
      price: 17.9,
      originalPrice: 20,
      users: 1,
      type: 'gpt',
      isPrivate: true
    },
    {
      id: 243,
      nameKey: 'homepage.comparisonTable.products.gptRenewal.name',
      loginMethodKey: 'homepage.comparisonTable.products.gptRenewal.loginMethod',
      privacyKey: 'homepage.comparisonTable.products.gptRenewal.privacy',
      price: 17.9,
      originalPrice: 20,
      users: 1,
      type: 'renew',
      isPrivate: true
    },
    {
      id: 246,
      nameKey: 'homepage.comparisonTable.products.gptShared3.name',
      loginMethodKey: 'homepage.comparisonTable.products.gptShared3.loginMethod',
      privacyKey: 'homepage.comparisonTable.products.gptShared3.privacy',
      price: 9.9,
      originalPrice: 20,
      users: 3,
      type: 'gpt',
      isPrivate: false
    },
    {
      id: 249,
      nameKey: 'homepage.comparisonTable.products.gptShared6.name',
      loginMethodKey: 'homepage.comparisonTable.products.gptShared6.loginMethod',
      privacyKey: 'homepage.comparisonTable.products.gptShared6.privacy',
      price: 4.9,
      originalPrice: 20,
      users: 6,
      type: 'gpt',
      isPrivate: false
    }
  ];

  const handleProductClick = (product) => {
    const twelveMonthProductId = get12MonthProductId(product.id);
    navigate(`/product/${twelveMonthProductId}?type=${product.type}`);
  };

  return (
    <section className="py-20 lg:py-28 bg-gradient-to-br from-purple-50/30 to-amber-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 标题部分 */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('homepage.comparisonTable.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('homepage.comparisonTable.subtitle')}
          </p>
        </div>

        {/* 表格容器 */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* 表格头部 */}
          <div className="bg-gradient-to-r from-purple-500 to-red-500 text-white">
            <div className="grid grid-cols-12 gap-4 p-6 font-semibold">
              <div className="col-span-3 text-center">{t('homepage.comparisonTable.headers.productName')}</div>
              <div className="col-span-2 text-center">{t('homepage.comparisonTable.headers.loginMethod')}</div>
              <div className="col-span-2 text-center">{t('homepage.comparisonTable.headers.dataPrivacy')}</div>
              <div className="col-span-2 text-center">{t('homepage.comparisonTable.headers.price')}</div>
              <div className="col-span-2 text-center">{t('homepage.comparisonTable.headers.originalPrice')}</div>
              <div className="col-span-1 text-center">{t('homepage.comparisonTable.headers.action')}</div>
            </div>
          </div>

          {/* 表格内容 */}
          <div className="divide-y divide-gray-200">
            {products.map((product, index) => (
              <div 
                key={product.id}
                className={`grid grid-cols-12 gap-4 p-6 hover:bg-gray-50 transition-colors ${
                  index % 2 === 0 ? 'bg-white' : 'bg-gray-25'
                }`}
              >
                {/* 产品名称 */}
                <div className="col-span-3 flex items-center">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-red-500 rounded-lg flex items-center justify-center">
                      <Globe className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 whitespace-pre-line text-sm">
                        {t(product.nameKey)}
                      </div>
                      {product.users > 1 && (
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <Users className="w-3 h-3 mr-1" />
                          {t('homepage.comparisonTable.userCount.users', { count: product.users })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* 登录方法 */}
                <div className="col-span-2 flex items-center justify-center">
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-2 mb-1">
                      <Globe className="w-4 h-4 text-blue-500" />
                      <Smartphone className="w-4 h-4 text-green-500" />
                    </div>
                    <div className="text-sm text-gray-600 whitespace-pre-line">
                      {t(product.loginMethodKey)}
                    </div>
                  </div>
                </div>

                {/* 数据隐私 */}
                <div className="col-span-2 flex items-center justify-center">
                  <div className="flex items-center space-x-2">
                    {product.isPrivate ? (
                      <Eye className="w-4 h-4 text-green-500" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-purple-500" />
                    )}
                    <span className={`text-sm ${
                      product.isPrivate ? 'text-green-600' : 'text-purple-600'
                    }`}>
                      {t(product.privacyKey)}
                    </span>
                  </div>
                </div>

                {/* 价格 */}
                <div className="col-span-2 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-500">
                      ${product.price}
                    </div>
                    <div className="text-sm text-gray-500">{t('common.month')}</div>
                  </div>
                </div>

                {/* 官网价格 */}
                <div className="col-span-2 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-xl text-gray-400 line-through">
                      ${product.originalPrice}
                    </div>
                    <div className="text-sm text-gray-500">{t('common.month')}</div>
                  </div>
                </div>

                {/* 操作按钮 */}
                <div className="col-span-1 flex items-center justify-center">
                  <button
                    onClick={() => handleProductClick(product)}
                    className="bg-gradient-to-r from-purple-500 to-red-500 text-white px-4 py-2 rounded-lg hover:from-purple-600 hover:to-red-600 transition-all duration-300 flex items-center space-x-2 text-sm font-semibold"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    <span>{t('homepage.comparisonTable.actions.select')}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 底部说明 */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>{t('homepage.comparisonTable.notes.pricing')}</p>
          <p>{t('homepage.comparisonTable.notes.privacy')}</p>
        </div>
      </div>
    </section>
  );
}